import { ORDER_STATUS_FILTERS } from "@bcc/components/orders/constants";
import { getOrderStatusCounts } from "@client-shared/services/api/orders";
import type { OrderStatusCountsData } from "@shared/schemas";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const ORDER_STATUS_COUNTS_QUERY_KEY = "orderStatusCounts";

const STALE_TIME_MS = 60_000;

interface OrderStatusCountsResult {
  readonly filterCounts: Record<string, number | undefined>;
  readonly isCountsLoading: boolean;
}

function deriveFilterCounts(data: OrderStatusCountsData): Record<string, number> {
  return Object.fromEntries(
    ORDER_STATUS_FILTERS.map(({ key, statuses }) => [key, statuses.reduce((sum, status) => sum + data[status], 0)])
  );
}

export function useOrderStatusCounts(vendorKey: string | undefined, customerAccountNumbers: string[]): OrderStatusCountsResult {
  const sortedAccounts = useMemo(
    () => [...customerAccountNumbers].sort((left, right) => left.localeCompare(right)),
    [customerAccountNumbers]
  );

  const { data, isLoading: isCountsLoading } = useQuery({
    queryKey: [ORDER_STATUS_COUNTS_QUERY_KEY, vendorKey, sortedAccounts],
    queryFn: () => {
      if (!vendorKey) throw new Error("vendorKey is required");
      return getOrderStatusCounts(vendorKey, sortedAccounts);
    },
    enabled: Boolean(vendorKey),
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const filterCounts = useMemo(() => (data ? deriveFilterCounts(data) : {}), [data]);

  return { filterCounts, isCountsLoading };
}
